import React, { useState, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Public contract signing page — /sign/:token
//
// Uses the main site's palette (not the CRM's) since this is what clients and
// contractors see. Renders the same numbered sections the PDF carries, then a
// type-to-sign form. No login: the unguessable token is the credential.
// ---------------------------------------------------------------------------

const p = {
  cream: '#faf8f3',
  beige: '#f5f1e8',
  brown: '#3d2b1f',
  espresso: '#2c2016',
  orange: '#b8560f',
  muted: '#7a6e60',
  border: '#d9d0c3',
  red: '#a84032',
  green: '#2a5c3f',
  white: '#ffffff',
};
const mono = `'Space Mono', 'Courier Prime', monospace`;

// `embedded` renders without the page shell (used inside the onboarding
// flow); `onSigned` fires after a successful signature.
export default function ContractSignPage({ token, embedded = false, onSigned }) {
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const [justSigned, setJustSigned] = useState(false);

  useEffect(() => {
    fetch(`/api/sign/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'This agreement could not be found.');
        setContract(data);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const sign = async () => {
    if (!signerName.trim() || !signatureText.trim()) {
      alert('Enter your printed name and type your signature.');
      return;
    }
    if (!agree) {
      alert('Please confirm you agree to the terms.');
      return;
    }
    setSigning(true);
    try {
      const r = await fetch(`/api/sign/${encodeURIComponent(token)}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signer_name: signerName, signer_title: signerTitle, signature_text: signatureText, agree: true }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Signing failed — please try again.');
      setJustSigned(true);
      setContract((c) => ({ ...c, status: 'signed', signed_at: data.signed_at, signer_name: signerName, signer_title: signerTitle }));
      if (onSigned) onSigned();
      if (!embedded) window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert(e.message);
    } finally {
      setSigning(false);
    }
  };

  const shell = (children) => embedded ? <div>{children}</div> : (
    <div style={{ minHeight: '100vh', background: p.cream, padding: '0 0 60px' }}>
      <style>{`
        * { box-sizing: border-box; } body { margin: 0; }
      `}</style>
      <header style={{ background: p.brown, borderBottom: `3px solid ${p.orange}`, padding: '20px 24px', textAlign: 'center' }}>
        <span style={{ fontFamily: mono, fontWeight: 700, fontSize: 22, letterSpacing: 5, color: p.cream, textTransform: 'uppercase' }}>HDDN LAB</span>
      </header>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px 0' }}>{children}</div>
    </div>
  );

  if (loading) {
    return shell(<p style={{ fontFamily: mono, color: p.muted, textAlign: 'center', letterSpacing: 2 }}>LOADING...</p>);
  }
  if (error) {
    return shell(
      <div style={{ background: p.white, border: `1px solid ${p.border}`, padding: 32, textAlign: 'center' }}>
        <p style={{ fontFamily: mono, color: p.red, fontSize: 15, margin: 0 }}>{error}</p>
        <p style={{ fontFamily: mono, color: p.muted, fontSize: 12, marginTop: 12 }}>
          If you think this is a mistake, reply to the email that sent you here or contact hi@hddnlab.com.
        </p>
      </div>
    );
  }

  const signed = contract.status === 'signed';
  const feeLabel = { month: '/month', project: '/project', hour: '/hour', day: '/day' }[contract.fee_interval] || '';

  return shell(
    <>
      {(signed || justSigned) && (
        <div style={{ background: p.green, color: p.white, padding: '16px 20px', marginBottom: 20, fontFamily: mono, fontSize: 14 }}>
          {justSigned
            ? '✓ Signed! A copy of the executed agreement is on its way to your inbox.'
            : `✓ This agreement was signed${contract.signer_name ? ` by ${contract.signer_name}` : ''}${contract.signed_at ? ` on ${new Date(contract.signed_at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} ET` : ''}.`}
          {' '}
          <a href={`/api/sign/${encodeURIComponent(token)}/pdf`} target="_blank" rel="noopener noreferrer" style={{ color: p.white, fontWeight: 700 }}>
            Download the signed PDF
          </a>
        </div>
      )}

      {/* Agreement header */}
      <div style={{ background: p.white, border: `1px solid ${p.border}`, borderTop: `4px solid ${p.orange}`, padding: '28px 30px', marginBottom: 16 }}>
        <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', color: p.orange, marginBottom: 8 }}>
          {contract.kind === 'contractor' ? 'Contractor Services & NDA Agreement' : 'Content Services Agreement'}
        </div>
        <h1 style={{ fontFamily: mono, fontSize: 22, color: p.espresso, margin: '0 0 6px', letterSpacing: 1 }}>{contract.title}</h1>
        {contract.fee_description && (
          <div style={{ fontFamily: mono, fontSize: 13, color: p.muted }}>{contract.fee_description}</div>
        )}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 18, paddingTop: 16, borderTop: `1px solid ${p.border}` }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: p.muted }}>Provider</div>
            <div style={{ fontFamily: mono, fontSize: 13, color: p.espresso, marginTop: 2 }}>{contract.provider_name}</div>
            <div style={{ fontFamily: mono, fontSize: 11, color: p.muted }}>{contract.provider_address}</div>
          </div>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: p.muted }}>
              {contract.kind === 'contractor' ? 'Contractor' : 'Client'}
            </div>
            <div style={{ fontFamily: mono, fontSize: 13, color: p.espresso, marginTop: 2 }}>{contract.party_name}</div>
            {contract.party_address && <div style={{ fontFamily: mono, fontSize: 11, color: p.muted }}>{contract.party_address}</div>}
          </div>
          {contract.fee_cents != null && (
            <div>
              <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: p.muted }}>Fee</div>
              <div style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, color: p.espresso, marginTop: 2 }}>
                ${(contract.fee_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}{feeLabel}
              </div>
            </div>
          )}
          {contract.effective_date && (
            <div>
              <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: p.muted }}>Effective</div>
              <div style={{ fontFamily: mono, fontSize: 13, color: p.espresso, marginTop: 2 }}>
                {new Date(String(contract.effective_date).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full agreement text */}
      <div style={{ background: p.white, border: `1px solid ${p.border}`, padding: '28px 30px', marginBottom: 16 }}>
        {(contract.sections || []).map((section, idx) => (
          <div key={idx} style={{ marginBottom: 22 }}>
            <h2 style={{ fontFamily: mono, fontSize: 13, letterSpacing: 2, color: p.brown, margin: '0 0 10px' }}>
              {idx + 1}. {section.heading}
            </h2>
            {section.body.map((para, j) => {
              const item = typeof para === 'string' ? { bullet: false, text: para } : para;
              if (item.row) {
                return (
                  <div key={j} style={{ display: 'grid', gridTemplateColumns: '42% 1fr', gap: 12, fontFamily: mono, fontSize: 12, lineHeight: 1.6, color: p.espresso, padding: '5px 0 5px 16px', borderBottom: `1px solid ${p.border}` }}>
                    <div style={{ fontWeight: 700 }}>{item.row[0]}</div>
                    <div>{item.row[1]}</div>
                  </div>
                );
              }
              return item.bullet ? (
                <div key={j} style={{ fontFamily: mono, fontSize: 12.5, lineHeight: 1.7, color: p.espresso, margin: '0 0 6px 16px' }}>
                  • {item.text}
                </div>
              ) : (
                <p key={j} style={{ fontFamily: mono, fontSize: 12.5, lineHeight: 1.7, color: p.espresso, margin: '0 0 10px' }}>
                  {item.text}
                </p>
              );
            })}
          </div>
        ))}
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <a href={`/api/sign/${encodeURIComponent(token)}/pdf`} target="_blank" rel="noopener noreferrer"
             style={{ fontFamily: mono, fontSize: 12, color: p.orange }}>
            View as PDF
          </a>
        </div>
      </div>

      {/* Signature form */}
      {!signed && !justSigned && (
        <div style={{ background: p.white, border: `1px solid ${p.border}`, borderTop: `4px solid ${p.brown}`, padding: '28px 30px' }}>
          <h2 style={{ fontFamily: mono, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', color: p.brown, margin: '0 0 18px' }}>
            Sign this agreement
          </h2>
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: p.muted, display: 'block', marginBottom: 4 }}>
                  Printed name *
                </label>
                <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                       style={{ width: '100%', padding: '10px 12px', fontFamily: mono, fontSize: 14, border: `1px solid ${p.border}`, background: p.cream, color: p.espresso, outline: 'none' }} />
              </div>
              <div>
                <label style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: p.muted, display: 'block', marginBottom: 4 }}>
                  Title (optional)
                </label>
                <input value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} placeholder="e.g. Owner"
                       style={{ width: '100%', padding: '10px 12px', fontFamily: mono, fontSize: 14, border: `1px solid ${p.border}`, background: p.cream, color: p.espresso, outline: 'none' }} />
              </div>
            </div>
            <div>
              <label style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: p.muted, display: 'block', marginBottom: 4 }}>
                Type your full name as your signature *
              </label>
              <input value={signatureText} onChange={(e) => setSignatureText(e.target.value)}
                     style={{
                       width: '100%', padding: '12px 14px', fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                       fontSize: 24, border: `1px solid ${p.border}`, borderBottom: `2px solid ${p.espresso}`,
                       background: p.beige, color: p.espresso, outline: 'none',
                     }} />
            </div>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontFamily: mono, fontSize: 12, lineHeight: 1.6, color: p.espresso, cursor: 'pointer' }}>
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                I have read and agree to the terms of this Agreement, and I intend my electronic signature to be as binding as a handwritten one.
              </span>
            </label>
            <button
              onClick={sign}
              disabled={signing}
              style={{
                padding: '14px 28px', background: p.orange, color: p.white, border: 'none', cursor: 'pointer',
                fontFamily: mono, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                opacity: signing ? 0.6 : 1,
              }}
            >
              {signing ? 'Signing...' : 'Agree & Sign'}
            </button>
          </div>
        </div>
      )}

      <p style={{ fontFamily: mono, fontSize: 10, color: p.muted, textAlign: 'center', marginTop: 24, letterSpacing: 1 }}>
        HDDN LAB · 72 Centre Avenue, New Rochelle, NY · hi@hddnlab.com
      </p>
    </>
  );
}
